'use client';

import React, {
  startTransition,
  useContext,
  useMemo,
  useState,
  ViewTransition,
} from 'react';

import { createPortal } from 'react-dom';

import transitions from './transitions.module.css';
import { type GuestLikeToastState } from './types';

import { GuestLikesToast } from '@/components/GuestLikesToast';
import { useIsClient } from '@/modules/dom/hooks/useIsClient';

const GuestLikesToastContext = React.createContext<{
  send: (state: GuestLikeToastState) => void;
  dismiss: () => void;
} | null>(null);

export function useGuestLikesToast() {
  const context = useContext(GuestLikesToastContext);

  if (!context) {
    throw new Error(
      'useGuestLikesToast must be used within a GuestLikesToastProvider'
    );
  }

  return context;
}

export function GuestLikesToastProvider({ children }: React.PropsWithChildren) {
  const [toast, setToast] = useState<{
    state: GuestLikeToastState;
    key: number;
  } | null>(null);
  const isClient = useIsClient();

  const value = useMemo(
    () => ({
      send: (state: GuestLikeToastState) =>
        startTransition(() =>
          setToast((prevState) => ({ state, key: (prevState?.key ?? 0) + 1 }))
        ),
      dismiss: () => startTransition(() => setToast(null)),
    }),
    []
  );

  return (
    <GuestLikesToastContext.Provider value={value}>
      {children}
      {isClient &&
        createPortal(
          <div aria-live="polite">
            {toast && (
              <ViewTransition
                name="guest-like-toast"
                enter={transitions.toastEnter}
                exit={transitions.toastExit}
                default="none"
              >
                <GuestLikesToast
                  key={toast.key}
                  {...toast.state}
                  dismiss={value.dismiss}
                />
              </ViewTransition>
            )}
          </div>,
          document.body
        )}
    </GuestLikesToastContext.Provider>
  );
}
